import { useState, useEffect, useCallback } from "react";
import CrmLayout from "@/components/feature/CrmLayout";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";
import {
  assignmentRules as initialRules,
  assignmentMethods,
  agentWorkloads as initialWorkloads,
  unassignedLeads as initialUnassigned,
  escalationRules as initialEscalation,
  assignmentLog as initialAssignmentLog,
} from "@/mocks/assignmentData";
import type { AssignmentRule, UnassignedLead, EscalationRule } from "@/mocks/assignmentData";
import RuleModal from "./components/RuleModal";

export default function AssignmentPage() {
  const { showToast } = useToast();

  const [method, setMethod] = useState("round_robin");
  const [rules, setRules] = useState<AssignmentRule[]>(initialRules);
  const [workloads, setWorkloads] = useState(initialWorkloads);
  const [unassigned, setUnassigned] = useState<UnassignedLead[]>(initialUnassigned);
  const [escalationRules, setEscalationRules] = useState<EscalationRule[]>(initialEscalation);
  const [assignmentLogs, setAssignmentLogs] = useState<any[]>(initialAssignmentLog);
  const [liveAgents, setLiveAgents] = useState<{ id: string; name: string; role?: string }[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AssignmentRule | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isBalancing, setIsBalancing] = useState(false);

  // Load dynamic configuration, live users, unassigned leads, and logs
  const fetchAllData = useCallback(() => {
    // 1. Fetch assignment config
    fetchApi<any>("/config/assignment-rules", undefined, null)
      .then((res) => {
        const data = res?.data || res;
        if (data) {
          if (data.method) setMethod(data.method);
          if (Array.isArray(data.rules) && data.rules.length > 0) setRules(data.rules);
          if (Array.isArray(data.escalationRules) && data.escalationRules.length > 0) {
            setEscalationRules(data.escalationRules);
          }
        }
      })
      .catch(() => {});

    // 2. Fetch live team members and lead counts
    Promise.all([
      fetchApi<any>("/auth/company-user/all", undefined, null),
      fetchApi<any>("/leads/stats", undefined, null),
    ])
      .then(([usersRes, statsRes]) => {
        const userList = Array.isArray(usersRes)
          ? usersRes
          : Array.isArray(usersRes?.items)
          ? usersRes.items
          : Array.isArray(usersRes?.data?.items)
          ? usersRes.data.items
          : Array.isArray(usersRes?.data)
          ? usersRes.data
          : [];

        const agentCounts = statsRes?.agentLeadCounts || statsRes?.data?.agentLeadCounts || {};

        if (userList.length > 0) {
          const agentsArray = userList.map((u: any, idx: number) => ({
            id: String(u.id || idx + 1),
            name: u.name || u.fullName || u.username || `Agent ${idx + 1}`,
            role: u.role || u.position || "Sales Agent",
          }));
          setLiveAgents(agentsArray);

          const liveWorkloads = userList.map((u: any, idx: number) => {
            const name = u.name || u.fullName || u.username || `Agent ${idx + 1}`;
            const count = agentCounts[name] !== undefined ? agentCounts[name] : (u.leadCount || 0);
            return {
              id: String(u.id || idx + 1),
              name,
              avatar: name.slice(0, 2).toUpperCase(),
              initialized: name.slice(0, 2).toUpperCase(),
              role: u.role || u.position || "Sales Agent",
              openLeads: count,
              status: u.isActive !== false ? ("available" as const) : ("offline" as const),
              maxCapacity: 50,
            };
          });
          setWorkloads(liveWorkloads);
        }
      })
      .catch(() => {});

    // 3. Fetch unassigned leads
    fetchApi<any>("/leads?limit=50", undefined, null)
      .then((res) => {
        const items = res?.items || res?.data?.items || (Array.isArray(res) ? res : []);
        const unassignedItems = items
          .filter((l: any) => !l.assigned || l.assigned === "Unassigned" || l.assigned.trim() === "")
          .map((l: any) => ({
            id: String(l.id),
            name: l.name,
            company: l.company || "Direct Inquiry",
            source: l.source || "Website",
            stage: l.stage || "New Inquiry",
            value: l.value || "₹0",
            waitTime: "Waiting",
            time: "Just now",
            score: l.score || 50,
          }));
        setUnassigned(unassignedItems);
      })
      .catch(() => {});

    // 4. Fetch activity logs
    fetchApi<any>("/activities?limit=15", undefined, null)
      .then((res) => {
        const items = res?.items || res?.data?.items || (Array.isArray(res) ? res : []);
        const logs = items
          .filter((a: any) => a.type?.includes("assign") || a.title?.toLowerCase().includes("assign") || a.description?.toLowerCase().includes("assign"))
          .map((a: any) => ({
            id: String(a.id),
            lead: a.lead_name || a.leadName || a.title || "Lead",
            assignedTo: a.user_name || a.userName || a.performed_by || "System",
            rule: a.detail || a.description || "Round Robin",
            timestamp: a.time_ago || a.created_at ? new Date(a.created_at).toLocaleTimeString() : "Just now",
          }));
        if (logs.length > 0) setAssignmentLogs(logs);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const saveConfig = useCallback(
    async (newMethod: string, newRules: AssignmentRule[], newEscalation: EscalationRule[]) => {
      try {
        await fetchApi("/config/assignment-rules", {
          method: "POST",
          body: JSON.stringify({
            method: newMethod,
            rules: newRules,
            escalationRules: newEscalation,
          }),
        });
      } catch (err: any) {
        console.error("Failed to save assignment config:", err);
      }
    },
    []
  );

  const maxLeads = Math.max(...workloads.map((w) => w.openLeads), 1);

  const handleToggleRule = (id: string) => {
    const updated = rules.map((r) => (r.id === id ? { ...r, active: !r.active } : r));
    setRules(updated);
    saveConfig(method, updated, escalationRules);
    showToast("Rule status updated", "success");
  };

  const handleEdit = (rule: AssignmentRule) => {
    setEditingRule(rule);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingRule(null);
    setModalOpen(true);
  };

  const handleDelete = (id: string) => {
    setDeleteConfirm(id);
  };

  const confirmDelete = () => {
    if (!deleteConfirm) return;
    const updated = rules.filter((r) => r.id !== deleteConfirm);
    setRules(updated);
    saveConfig(method, updated, escalationRules);
    showToast("Rule deleted successfully", "success");
    setDeleteConfirm(null);
  };

  const handleSaveRule = (data: Omit<AssignmentRule, "id">, id?: string) => {
    let updated: AssignmentRule[];
    if (id) {
      updated = rules.map((r) => (r.id === id ? { ...r, ...data, id } : r));
      showToast("Rule updated successfully", "success");
    } else {
      const newRule: AssignmentRule = {
        ...data,
        id: String(Date.now()),
      };
      updated = [...rules, newRule];
      showToast("Rule created successfully", "success");
    }
    setRules(updated);
    saveConfig(method, updated, escalationRules);
    setModalOpen(false);
  };

  const handleSaveMethod = () => {
    setIsSaving(true);
    saveConfig(method, rules, escalationRules)
      .then(() => {
        showToast("Assignment method saved successfully", "success");
      })
      .finally(() => setIsSaving(false));
  };

  const handleAutoBalance = async () => {
    setIsBalancing(true);
    try {
      const agentNames = liveAgents.map((a) => a.name);
      await fetchApi("/leads/auto-assign", {
        method: "PATCH",
        body: JSON.stringify({ agents: agentNames }),
      });
      showToast("Leads auto-balanced across team", "success");
      fetchAllData();
    } catch (err: any) {
      showToast(err.message || "Auto-balance failed", "error");
    } finally {
      setIsBalancing(false);
    }
  };

  const handleReassign = async (leadId: string, agentName: string) => {
    try {
      await fetchApi(`/leads/${leadId}`, {
        method: "PATCH",
        body: JSON.stringify({ assigned: agentName }),
      });
      setUnassigned((prev) => prev.filter((l) => l.id !== leadId));
      showToast(`Lead reassigned to ${agentName}`, "success");
      fetchAllData();
    } catch (err: any) {
      showToast(err.message || "Failed to reassign lead", "error");
    }
  };

  const handleToggleEscalation = (id: string) => {
    const updated = escalationRules.map((r) => (r.id === id ? { ...r, active: !r.active } : r));
    setEscalationRules(updated);
    saveConfig(method, rules, updated);
    showToast("Escalation rule updated", "success");
  };

  const getConditionSummary = (rule: AssignmentRule): string => {
    if (rule.isFallback) return "No rule matched → Round Robin across all agents";
    const { condition, action } = rule;
    const agentLabel = action.type === "round_robin" ? "Round Robin" : action.agentName;
    return `If ${condition.field} ${condition.operator} ${condition.value} → Assign to ${agentLabel}`;
  };

  return (
    <CrmLayout title="Assignment Rules">
      <div className="p-5 md:p-6 space-y-5 max-w-[1300px] mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Assignment Rules</h1>
            <p className="text-sm text-gray-500 mt-0.5">Automate how leads are assigned to your team</p>
          </div>
          <button
            onClick={handleAdd}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all duration-200 active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-sm"
          >
            <i className="ri-add-line" />
            Add Rule
          </button>
        </div>

        {/* SECTION 1: Assignment Method Selector */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <h2 className="text-base font-semibold text-gray-900 mb-1">Default Assignment Method</h2>
          <p className="text-sm text-gray-500 mb-5">Select default strategy for distributing unassigned leads</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {assignmentMethods.map((m) => {
              const isSelected = method === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethod(m.id)}
                  className={`flex items-start gap-4 p-4 rounded-xl text-left transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? "border-2 border-indigo-600 bg-indigo-50/70 shadow-sm"
                      : "border border-gray-200 bg-white hover:border-indigo-300"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${m.iconBg}`}>
                    <i className={`${m.icon} text-lg`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{m.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{m.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>
          <button
            onClick={handleSaveMethod}
            disabled={isSaving}
            className="mt-5 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-sm font-semibold text-white transition-all duration-200 active:scale-[0.98] whitespace-nowrap cursor-pointer shadow-sm disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save Method"}
          </button>
        </div>

        {/* SECTION 2: Assignment Rules List */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <h2 className="text-base font-semibold text-gray-900">Active Rules</h2>
              <span className="px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold border border-indigo-100">
                {rules.length}
              </span>
            </div>
            <button
              onClick={handleAdd}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line text-sm" />
              Add Rule
            </button>
          </div>

          <div className="divide-y divide-gray-100">
            {rules.map((rule) => (
              <div
                key={rule.id}
                className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/60 transition-colors"
              >
                {/* Active toggle button (Pixel-Perfect UI) */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={rule.active}
                  onClick={() => handleToggleRule(rule.id)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                    rule.active ? "bg-indigo-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      rule.active ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>

                {/* Rule info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{rule.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{getConditionSummary(rule)}</p>
                </div>

                {/* Priority / Fallback badge */}
                {rule.isFallback ? (
                  <span className="px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold whitespace-nowrap flex-shrink-0 border border-amber-100">
                    Fallback
                  </span>
                ) : (
                  <span className="px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-semibold whitespace-nowrap flex-shrink-0">
                    Priority {rule.priority}
                  </span>
                )}

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleEdit(rule)}
                    className="p-2 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors cursor-pointer"
                    title="Edit"
                  >
                    <i className="ri-edit-line text-base" />
                  </button>
                  {!rule.isFallback && (
                    <button
                      onClick={() => handleDelete(rule.id)}
                      className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors cursor-pointer"
                      title="Delete"
                    >
                      <i className="ri-delete-bin-line text-base" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 4: Agent Workload Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-gray-900">Current Agent Workload</h2>
            <p className="text-sm text-gray-500">Open leads assigned per agent</p>
          </div>

          <div className="space-y-3.5">
            {workloads.map((w) => {
              const pct = Math.round((w.openLeads / maxLeads) * 100);
              return (
                <div key={w.id} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 flex-shrink-0">
                    {w.initialized || w.avatar || w.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-semibold text-gray-900 truncate">{w.name}</span>
                        <span className="text-[11px] text-gray-400 flex-shrink-0">{w.role}</span>
                      </div>
                      <span className="text-xs text-indigo-700 font-bold flex-shrink-0 ml-2 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100">
                        {w.openLeads} open leads
                      </span>
                    </div>
                    <div className="w-full h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-indigo-600 transition-all duration-500"
                        style={{ width: `${Math.max(5, pct)}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={handleAutoBalance}
            disabled={isBalancing}
            className="mt-5 w-full px-4 py-3 rounded-xl border border-indigo-300 bg-white text-sm font-semibold text-indigo-600 hover:bg-indigo-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
          >
            {isBalancing ? "Auto-balancing Leads..." : "Auto-balance now"}
          </button>
        </div>

        {/* SECTION 5: Manual Reassignment */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <h2 className="text-base font-semibold text-gray-900">Manual Reassignment</h2>
              <span className="px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-100">
                {unassigned.length} unassigned
              </span>
            </div>
          </div>

          {unassigned.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-check-line text-green-600 text-xl" />
              </div>
              <p className="text-sm font-semibold text-gray-900">All leads assigned</p>
              <p className="text-xs text-gray-500 mt-0.5">No unassigned leads waiting</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {unassigned.map((lead) => (
                <div
                  key={lead.id}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/60 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900 truncate">{lead.name}</span>
                      <span className="text-xs text-gray-400">· {lead.source}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-gray-500">{lead.stage}</span>
                      <span className="text-xs text-amber-600 font-medium">Waiting</span>
                      <span className="text-xs text-gray-400">{lead.value}</span>
                    </div>
                  </div>
                  <select
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val) handleReassign(lead.id, val);
                    }}
                    defaultValue=""
                    className="px-3.5 py-2 rounded-xl border border-gray-200 bg-white text-xs text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <option value="" disabled>Assign to...</option>
                    {liveAgents.map((a) => (
                      <option key={a.id} value={a.name}>{a.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SECTION 6: Escalation Rules */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <h2 className="text-base font-semibold text-gray-900">Escalation Rules</h2>
              <span className="px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                {escalationRules.filter((r) => r.active).length} active
              </span>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {escalationRules.map((rule) => (
              <div
                key={rule.id}
                className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/60 transition-colors"
              >
                {/* Active toggle button (Pixel-Perfect UI) */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={rule.active}
                  onClick={() => handleToggleEscalation(rule.id)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                    rule.active ? "bg-indigo-600" : "bg-gray-200"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      rule.active ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{rule.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    When: {rule.trigger}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    Then: {rule.action}
                  </p>
                </div>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap flex-shrink-0 ${
                    rule.active ? "bg-green-50 text-green-700 border border-green-100" : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {rule.active ? "Active" : "Inactive"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 7: Assignment Log */}
        <div id="assignment-log" className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Assignment Log</h2>
              <p className="text-xs text-gray-500 mt-0.5">Recent lead assignment activity</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase text-gray-500 tracking-wider">
                  <th className="text-left px-5 py-3">Lead</th>
                  <th className="text-left px-5 py-3">Assigned To</th>
                  <th className="text-left px-5 py-3">Rule / Method</th>
                  <th className="text-left px-5 py-3">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {assignmentLogs.map((entry, idx) => (
                  <tr key={entry.id || idx} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-3.5">
                      <span className="text-sm font-semibold text-gray-900">{entry.lead}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-sm text-gray-700 font-medium">{entry.assignedTo}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      {entry.rule ? (
                        <span className="px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-semibold whitespace-nowrap border border-indigo-100">
                          {entry.rule}
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-semibold whitespace-nowrap">
                          {entry.method || "Auto-assign"}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs text-gray-400">{entry.timestamp}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Rule Modal */}
        {modalOpen && (
          <RuleModal
            rule={editingRule}
            onClose={() => setModalOpen(false)}
            onSave={handleSaveRule}
            existingRules={rules}
            liveAgents={liveAgents}
          />
        )}

        {/* Delete confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 animate-fade-in">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl border border-gray-200 animate-slide-in">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-red-600 text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Delete Rule</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              <div className="flex items-center gap-2.5 justify-end pt-2">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-sm font-semibold text-white transition-colors whitespace-nowrap cursor-pointer shadow-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </CrmLayout>
  );
}